import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn } from "@/components/Motion";
import { GALLERY_SCENES, POSTERS, PawIcon } from "@/lib/brand";

const ALL_ART = [...GALLERY_SCENES, ...POSTERS];

export default function Gallery() {
    const [active, setActive] = useState(null);

    const prev = () => setActive((i) => (i - 1 + ALL_ART.length) % ALL_ART.length);
    const next = () => setActive((i) => (i + 1) % ALL_ART.length);

    return (
        <div data-testid="gallery-page">
            <PageHero
                eyebrow="Brand Gallery"
                titleLines={["Nine scenes.", "One mascot."]}
                copy="The official PURP ALERT mascot in every lane — faith, gaming, studio, film, stage, canvas, and the payoff. Black cap, cross, braids, grill, chains. No crowns. No compromises."
            />

            <section className="py-16 md:py-24 border-t border-white/10" data-testid="gallery-grid-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {GALLERY_SCENES.map((s, i) => (
                            <FadeIn key={s.title} delay={0.05 * (i % 6)}>
                                <button
                                    onClick={() => setActive(i)}
                                    className="group block w-full text-left rounded-2xl overflow-hidden border border-white/10 hover:border-purp/50 transition-colors duration-300 bg-panel/75"
                                    data-testid={`gallery-scene-${i}`}
                                >
                                    <div className="relative aspect-square overflow-hidden">
                                        <img
                                            src={s.src}
                                            alt={`PURP ALERT mascot — ${s.title}`}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    </div>
                                    <div className="flex items-center justify-between px-5 py-4">
                                        <p className="font-display font-bold uppercase text-xl">{s.title}</p>
                                        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 flex items-center gap-2">
                                            <PawIcon className="w-3.5 h-3.5 text-purp/60" /> {String(i + 1).padStart(2, "0")}/09
                                        </p>
                                    </div>
                                </button>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* POSTER SERIES */}
            <section className="py-16 md:py-24 border-t border-white/10" data-testid="poster-series">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.35em] text-purp-neon flex items-center gap-3 mb-5">
                            <span className="inline-block w-2 h-2 rounded-full bg-volt animate-pulse-dot" /> Poster Series
                        </p>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            PURP ALERT <span className="italic text-purp">on the wall.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {POSTERS.map((p, i) => (
                            <FadeIn key={p.title} delay={0.05 * (i % 6)}>
                                <button
                                    onClick={() => setActive(GALLERY_SCENES.length + i)}
                                    className="group block w-full text-left rounded-2xl overflow-hidden border border-white/10 hover:border-purp/50 transition-colors duration-300 bg-panel/75"
                                    data-testid={`poster-${i}`}
                                >
                                    <div className="relative aspect-[2/3] overflow-hidden">
                                        <img
                                            src={p.src}
                                            alt={`PURP ALERT NETWORK poster — ${p.title}`}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between px-4 py-3">
                                        <p className="font-display font-bold uppercase text-lg">{p.title}</p>
                                        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">P{String(i + 1).padStart(2, "0")}</p>
                                    </div>
                                </button>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <AnimatePresence>
                {active !== null && (
                    <motion.div
                        className="fixed inset-0 z-[100] bg-obsidian/90 backdrop-blur-xl flex items-center justify-center p-5"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                        data-testid="gallery-lightbox"
                    >
                        <button
                            className="absolute top-5 right-5 text-slate-300 hover:text-purp-neon transition-colors p-2"
                            onClick={() => setActive(null)}
                            data-testid="gallery-lightbox-close"
                            aria-label="Close"
                        >
                            <X className="w-7 h-7" />
                        </button>
                        <button
                            className="absolute left-3 md:left-8 text-slate-300 hover:text-purp-neon transition-colors p-2"
                            onClick={(e) => { e.stopPropagation(); prev(); }}
                            data-testid="gallery-lightbox-prev"
                            aria-label="Previous scene"
                        >
                            <ChevronLeft className="w-9 h-9" />
                        </button>
                        <motion.figure
                            key={active}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="max-w-2xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={ALL_ART[active].src}
                                alt={`PURP ALERT artwork — ${ALL_ART[active].title}`}
                                className="w-full max-h-[70vh] object-contain rounded-3xl border border-purp/40 card-glow"
                            />
                            <figcaption className="mt-5 flex items-center justify-between">
                                <span className="font-display font-extrabold uppercase text-2xl md:text-3xl">
                                    {ALL_ART[active].title}
                                </span>
                                <span className="font-mono text-xs tracking-[0.3em] text-purp-neon">
                                    {String(active + 1).padStart(2, "0")} / {ALL_ART.length}
                                </span>
                            </figcaption>
                        </motion.figure>
                        <button
                            className="absolute right-3 md:right-8 text-slate-300 hover:text-purp-neon transition-colors p-2"
                            onClick={(e) => { e.stopPropagation(); next(); }}
                            data-testid="gallery-lightbox-next"
                            aria-label="Next scene"
                        >
                            <ChevronRight className="w-9 h-9" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
